import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Trash2 } from "lucide-react";
import { AlertMessage, Card, CardContent, CardHeader, CardTitle, ModernButton, ModernInput, Toast, ToastType } from "../../components/ui";
import { apiService } from "../../services/api";

type LeadSourceItem = {
  id: string;
  key: string;
  label: string;
  color?: string | null;
  isOfficial: boolean;
  isActive: boolean;
  sortOrder: number;
};

type LeadMediaCatalogItem = {
  id: string;
  key: string;
  label: string;
  color?: string | null;
  tier: "spotlight" | "standard";
  sortOrder: number;
  isSelected: boolean;
};

function sortGlobalCatalog(items: LeadMediaCatalogItem[]): LeadMediaCatalogItem[] {
  return [...items].sort((a, b) => {
    const pa = a.tier === "spotlight" ? 0 : 1;
    const pb = b.tier === "spotlight" ? 0 : 1;
    if (pa !== pb) return pa - pb;
    if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
    return a.label.localeCompare(b.label);
  });
}

type Draft = {
  key: string;
  label: string;
  color: string;
  isActive: boolean;
  sortOrder: number;
};

const emptyDraft: Draft = {
  key: "",
  label: "",
  color: "#3B82F6",
  isActive: true,
  sortOrder: 0,
};

export const LeadSourcesSettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const [globalCatalog, setGlobalCatalog] = useState<LeadMediaCatalogItem[]>([]);
  const [customMedia, setCustomMedia] = useState<LeadSourceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [catalog, sources] = await Promise.all([
        apiService.getLeadMediaCatalog(),
        apiService.getLeadSources(),
      ]);
      setGlobalCatalog(sortGlobalCatalog(Array.isArray(catalog) ? catalog : []));
      setCustomMedia(Array.isArray(sources) ? sources as any : []);
    } catch {
      setError(t("leadMediaSettings.errors.load"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const resetDraft = () => setDraft(emptyDraft);

  const saveDraft = async () => {
    if (!draft.key.trim() || !draft.label.trim()) {
      setToast({ message: t("leadMediaSettings.errors.required"), type: "error" });
      return;
    }
    try {
      setSaving(true);
      await apiService.createLeadMedia({
        key: draft.key.trim(),
        label: draft.label.trim(),
        color: draft.color.trim() || undefined,
        isActive: draft.isActive,
        sortOrder: Number(draft.sortOrder) || 0,
      });
      await load();
      resetDraft();
      setToast({ message: t("leadMediaSettings.success.saved"), type: "success" });
    } catch {
      setToast({ message: t("leadMediaSettings.errors.save"), type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const deleteSource = async (id: string) => {
    try {
      setSaving(true);
      await apiService.deleteLeadMedia(id);
      await load();
      setToast({ message: t("leadMediaSettings.success.deleted"), type: "success" });
    } catch {
      setToast({ message: t("leadMediaSettings.errors.delete"), type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <div>
        <h1 className="text-2xl font-bold text-foreground">{t("leadMediaSettings.title")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("leadMediaSettings.subtitle")}
        </p>
      </div>

      {error ? <AlertMessage variant="error" message={error} /> : null}

      <Card>
        <CardHeader>
          <CardTitle>{t("leadMediaSettings.createTitle")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ModernInput
              label={t("leadMediaSettings.fields.key")}
              value={draft.key}
              onChange={(e) => setDraft((prev) => ({ ...prev, key: e.target.value }))}
              placeholder="ex.: instagram_ads"
            />
            <ModernInput
              label={t("leadMediaSettings.fields.label")}
              value={draft.label}
              onChange={(e) => setDraft((prev) => ({ ...prev, label: e.target.value }))}
              placeholder="ex.: Instagram Ads"
            />
            <ModernInput
              label={t("leadMediaSettings.fields.color")}
              value={draft.color}
              onChange={(e) => setDraft((prev) => ({ ...prev, color: e.target.value }))}
              placeholder="#3B82F6"
            />
            <ModernInput
              label={t("leadMediaSettings.fields.sortOrder")}
              type="number"
              value={String(draft.sortOrder)}
              onChange={(e) => setDraft((prev) => ({ ...prev, sortOrder: Number(e.target.value) || 0 }))}
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              checked={draft.isActive}
              onChange={(e) => setDraft((prev) => ({ ...prev, isActive: e.target.checked }))}
            />
            {t("leadMediaSettings.fields.active")}
          </label>
          <div className="flex items-center gap-2">
            <ModernButton type="button" onClick={() => void saveDraft()} disabled={saving}>
              <Plus className="h-4 w-4 mr-2" />
              {t("leadMediaSettings.actions.create")}
            </ModernButton>
            <ModernButton type="button" variant="outline" onClick={resetDraft} disabled={saving}>
              {t("leadMediaSettings.actions.clear")}
            </ModernButton>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("leadMediaSettings.sections.catalogGlobal")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {loading ? (
            <p className="text-sm text-muted-foreground">{t("actions.loading")}</p>
          ) : globalCatalog.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("leadMediaSettings.emptyCatalog")}</p>
          ) : (
            globalCatalog.map((item) => (
              <div key={item.id} className="rounded-md border border-border p-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground truncate">{item.label}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {t("leadMediaSettings.metaCatalog", {
                      key: item.key,
                      order: item.sortOrder,
                      tier: t(`leadMediaSettings.tier.${item.tier}`),
                    })}
                  </p>
                </div>
                <span className="text-xs text-muted-foreground">{t("leadMediaSettings.readOnly")}</span>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("leadMediaSettings.sections.custom")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {loading ? (
            <p className="text-sm text-muted-foreground">{t("actions.loading")}</p>
          ) : customMedia.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("leadMediaSettings.emptyCustom")}</p>
          ) : (
            customMedia.map((item) => (
              <div key={item.id} className="rounded-md border border-border p-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground truncate">{item.label}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {t("leadMediaSettings.meta", {
                      key: item.key,
                      order: item.sortOrder,
                      status: item.isActive ? t("leadMediaSettings.active") : t("leadMediaSettings.inactive"),
                    })}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <ModernButton type="button" variant="outline" size="sm" onClick={() => void deleteSource(item.id)} disabled={saving}>
                    <Trash2 className="h-4 w-4" />
                  </ModernButton>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
};
